import { Component, inject, Inject, signal, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { AbbModalComponent } from '@components/abb-modal/abb-modal.component';
import { MODAL_DATA, ModalData } from '@components/abb-modal/modal';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { UserDataTable } from '../users.component';
import { UserFormComponent } from '../user-form/user-form.component';
import { Apollo } from 'apollo-angular';
import { updateUserMutation } from '@graphql/user';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { UserSettingsService } from '@services/user/user-settings.service';
import {
  PermissionInput,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from 'gql/graphql';
import { t } from '@utils/strings';

export type UserModalData = {
  mode: 'new' | 'edit';
  user?: UserDataTable;
};

@Component({
  selector: 'app-user-modal',
  standalone: true,
  template: `
    <abb-modal [title]="title[data.mode] | t" #modalRef>
      <abb-user-form
        [mode]="data.mode"
        [initValues]="initValues"
        (formSubmit)="onSubmit($event)"
        (formValidationStatusChange)="handleSubmitStatusBtn($event)"
      ></abb-user-form>
      <div footer class="w-full flex flex-row gap-8 justify-end">
        <abb-button
          variant="clean"
          [disabled]="saving()"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        <abb-button
          class="w-32"
          type="submit"
          form="user-form"
          [loading]="saving()"
          [disabled]="submitBtnDisabled || saving()"
          >{{ 'Save' | t }}</abb-button
        >
      </div>
    </abb-modal>
  `,
  styles: ``,
  imports: [AbbModalComponent, TPipe, AbbButtonComponent, UserFormComponent],
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.users' }],
  host: { class: 'flex flex-1 min-h-0 flex-col' },
})
export class UserModalComponent implements ModalData<UserModalData> {
  @ViewChild('modalRef') modalRef: AbbModalComponent | undefined;
  prefix = inject(TRANSLATION_PREFIX);
  constructor(
    @Inject(MODAL_DATA) public data: UserModalData,
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService,
    private readonly userSettingsService: UserSettingsService
  ) {}
  readonly title = {
    edit: 'Edit',
    new: 'New',
  };
  readonly initValues =
    this.data.mode == 'edit'
      ? {
          id: this.data.user?.id!,
          name: this.data.user?.name!,
          lastName: this.data.user?.lastName!,
          email: this.data.user?.email!,
          propertyIds: this.data.user?.propertyIds!,
          role: this.data.user?.role!,
          imgUrl: this.data.user?.imgUrl,
          commissionerEndDate: this.data.user?.commissionerEndDate ?? null,
          commissionerPermissions:
            this.data.user?.commissionerPermissions ?? undefined,
        }
      : undefined;

  submitBtnDisabled: boolean = true;
  handleSubmitStatusBtn(disabled: boolean): void {
    this.submitBtnDisabled = disabled;
  }

  onCancel() {
    this.modalRef?.dispose();
  }

  saving = signal(false);
  onSubmit(form: FormGroup): void {
    if (form.invalid) {
      return;
    }
    this.saving.set(true);
    const commissionerPermissionsCtrl = form.get('commissionerPermissions');
    let mappedCommissionerPermissions:
      | {
          key: string;
          value: PermissionInput;
        }[]
      | undefined;
    if (commissionerPermissionsCtrl && commissionerPermissionsCtrl?.value) {
      mappedCommissionerPermissions = Object.entries(
        commissionerPermissionsCtrl?.value
      ).map(([key, value]) => {
        return {
          key,
          value: value as PermissionInput,
        };
      });
    }
    this.apollo
      .mutate<UpdateUserMutation, UpdateUserMutationVariables>({
        mutation: updateUserMutation,
        variables: {
          user: {
            id: this.data.user?.id!,
            name: form.value.name,
            lastName: form.value.lastName,
            email: form.value.email,
            propertyIds: form.value.propertyIds,
            role: form.value.role,
            commissionerEndDate: form.value.commissionerEndDate ?? null,
            commissionerPermissions: mappedCommissionerPermissions ?? undefined,
          },
        },
      })
      .subscribe({
        next: (result) => {
          this.saving.set(false);
          this.toastService.notifySuccess(
            t(this.prefix, `{name} was updated successfully`, this.translate, {
              name: form.value.name + ' ' + form.value.lastName,
            })
          );
          const currentUserAccount = this.userSettingsService.userAccount();
          if (currentUserAccount && result.data?.updateUser?.email === currentUserAccount.email) {
            this.userSettingsService.getUserAccount();
          }
          
          this.modalRef?.dispose();
          this.apollo.client.refetchQueries({
            include: ['UserList'],
          });
        },
        error: () => {
          this.saving.set(false);
          this.toastService.notifyError(
            t(
              this.prefix,
              `An error occurred while updating {name}`,
              this.translate,
              { name: form.value.name + ' ' + form.value.lastName }
            )
          );
        },
      });
  }
}
